// NetplanWifiManager.jsx
import React, { useEffect, useState } from "react";
import wifiApi from "./systemWifiApi";
import "./netplan-wifi-manager.kvin.css";
import "./netplan-wifi-manager.kvin-diag.css";

export default function NetplanWifiManager() {
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [addForm, setAddForm] = useState({ ssid: "", password: "" });
  const [updateForm, setUpdateForm] = useState({ ssid: "", password: "" });

  // NEW: diagnostics state
  const [rttMs, setRttMs] = useState(null);
  const [rttLoading, setRttLoading] = useState(false);
  const [speed, setSpeed] = useState(null);
  const [speedLoading, setSpeedLoading] = useState(false);

  const notify = (m) => {
    setMessage(m);
    setTimeout(() => setMessage(null), 3000);
  };

  async function load() {
    setLoading(true);
    try {
      const { data } = await wifiApi.getInfo();
      setConnected(data.connected || null);
      const wifis = data.config?.wifis || data.config?.network?.wifis || {};
      const aps = (wifis.wlan0 || {})["access-points"] || {};
      setNetworks(Object.keys(aps));
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const handleRefresh = async () => {
    setLoading(true);
    try {
      const { data } = await wifiApi.refresh();
      setConnected(data.connected || null);
      notify("Refreshed.");
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (ssid) => {
    setLoading(true);
    try {
      const { data } = await wifiApi.connect(ssid);
      setConnected(data.connected || ssid);
      notify(`Connecting to ${ssid}…`);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.ssid || !addForm.password)
      return setError("SSID + password required.");
    setLoading(true);
    try {
      await wifiApi.add(addForm.ssid.trim(), addForm.password);
      setAddForm({ ssid: "", password: "" });
      notify("Network added.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!updateForm.ssid || !updateForm.password)
      return setError("SSID + new password required.");
    setLoading(true);
    try {
      await wifiApi.update(updateForm.ssid, updateForm.password);
      setUpdateForm({ ssid: "", password: "" });
      notify("Password updated.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ssid) => {
    if (!window.confirm(`Delete "${ssid}"?`)) return;
    setLoading(true);
    try {
      await wifiApi.remove(ssid);
      notify("Deleted.");
      await load();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  // ------- NEW: diagnostics handlers -------
  const runPingTest = async () => {
    setRttLoading(true);
    setError(null);
    try {
      // 5 samples, average
      const samples = [];
      for (let i = 0; i < 5; i++) {
        const t0 = performance.now();
        await wifiApi.pingTest(); // tiny GET
        const dt = performance.now() - t0;
        samples.push(dt);
      }
      const avg = Math.round(
        samples.reduce((a, b) => a + b, 0) / samples.length
      );
      setRttMs(avg);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setRttLoading(false);
    }
  };

  const runServerSpeedTest = async () => {
    setSpeedLoading(true);
    setError(null);
    try {
      const { data } = await wifiApi.getServerInternetSpeed();
      setSpeed(data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSpeedLoading(false);
    }
  };

  return (
    <div className="kvinNetMan_shell">
      <header className="kvinNetMan_header">
        <div className="kvinNetMan_title">Pi Wi-Fi Manager</div>
        <div className="kvinNetMan_status">
          <span
            className={`kvinNetMan_dot ${
              connected ? "kvinNetMan_dot--on" : "kvinNetMan_dot--off"
            }`}
          />
          <span>{connected ? `Connected: ${connected}` : "Not connected"}</span>
          <button
            className="kvinNetMan_btn"
            disabled={loading}
            onClick={handleRefresh}
          >
            {loading ? "Working…" : "Refresh"}
          </button>
        </div>
      </header>

      {message && <div className="kvinNetMan_toast knj_fadeIn">{message}</div>}
      {error && <div className="kvinNetMan_error knj_fadeIn">{error}</div>}

      {/* Diagnostics */}
      <section className="kvinNetMan_diag">
        <div className="kvinNetMan_diagHeader">
          <h2 className="kvinNetMan_diagTitle">Network Diagnostics</h2>
          <div className="kvinNetMan_diagHint">
            Quick tests for latency & throughput
          </div>
        </div>

        <div className="kvinNetMan_diagGrid">
          {/* RTT card */}
          <div className="kvinNetMan_card">
            <div className="kvinNetMan_cardHeader">
              <span className="kvinNetMan_cardTitle">
                Website → Server Unit
              </span>
              <button
                className={`kvinNetMan_btnPrimary ${
                  rttLoading ? "kvinNetMan_btn--loading" : ""
                }`}
                onClick={runPingTest}
                disabled={rttLoading}
              >
                {rttLoading ? "Pinging…" : "Run Test"}
              </button>
            </div>
            <div className="kvinNetMan_metric">
              <span className="kvinNetMan_metricValue">
                {rttMs != null ? rttMs : "—"}
              </span>
              <span className="kvinNetMan_metricUnit">ms</span>
            </div>
            <div className="kvinNetMan_subtle">
              Round-trip time to this server
            </div>
          </div>

          {/* Server speed card */}
          <div className="kvinNetMan_card">
            <div className="kvinNetMan_cardHeader">
              <span className="kvinNetMan_cardTitle">
                Server Unit - Internet Speed
              </span>
              <button
                className={`kvinNetMan_btnPrimary ${
                  speedLoading ? "kvinNetMan_btn--loading" : ""
                }`}
                onClick={runServerSpeedTest}
                disabled={speedLoading}
              >
                {speedLoading ? "Testing…" : "Run Test"}
              </button>
            </div>

            {speed?.ok ? (
              <div className="kvinNetMan_speedGrid">
                <div className="kvinNetMan_metric">
                  <span className="kvinNetMan_metricValue">
                    {speed.ping_ms ?? "—"}
                  </span>
                  <span className="kvinNetMan_metricUnit">ms</span>
                  <span className="kvinNetMan_metricLabel">Ping</span>
                </div>
                <div className="kvinNetMan_metric">
                  <span className="kvinNetMan_metricValue">
                    {speed.download_Mbps ?? "—"}
                  </span>
                  <span className="kvinNetMan_metricUnit">Mbps</span>
                  <span className="kvinNetMan_metricLabel">Download</span>
                </div>
                <div className="kvinNetMan_metric">
                  <span className="kvinNetMan_metricValue">
                    {speed.upload_Mbps ?? "—"}
                  </span>
                  <span className="kvinNetMan_metricUnit">Mbps</span>
                  <span className="kvinNetMan_metricLabel">Upload</span>
                </div>
              </div>
            ) : speed ? (
              <div className="kvinNetMan_errorInline">
                {speed.error || "Speed test failed"}
              </div>
            ) : (
              <div className="kvinNetMan_placeholder">No results yet</div>
            )}

            {speed?.server && (
              <div className="kvinNetMan_meta">
                <span className="kvinNetMan_metaItem">
                  Server: {speed.server.name} ({speed.server.location})
                </span>
                {speed.isp && (
                  <span className="kvinNetMan_metaItem">ISP: {speed.isp}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="kvinNetMan_section">
        <h2 className="kvinNetMan_h2">Saved Networks</h2>
        {networks.length === 0 ? (
          <div className="kvinNetMan_empty">No saved Wi-Fi networks.</div>
        ) : (
          <ul className="kvinNetMan_list">
            {networks.map((ssid) => (
              <li key={ssid} className="kvinNetMan_item">
                <div className="kvinNetMan_itemMain">
                  <div className="kvinNetMan_ssid">{ssid}</div>
                  {connected === ssid && (
                    <span className="kvinNetMan_badge">current</span>
                  )}
                </div>
                <div className="kvinNetMan_itemActions">
                  <button
                    className="kvinNetMan_btn"
                    onClick={() => handleConnect(ssid)}
                    disabled={loading}
                  >
                    Connect
                  </button>
                  <button
                    className="kvinNetMan_btn kvinNetMan_btn--danger"
                    onClick={() => handleDelete(ssid)}
                    disabled={loading}
                  >
                    Delete
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="kvinNetMan_section">
        <h2 className="kvinNetMan_h2">Add Network</h2>
        <form
          className="kvinNetMan_form"
          onSubmit={handleAdd}
          autoComplete="off"
        >
          <div className="kvinNetMan_field">
            <label className="kvinNetMan_label">SSID</label>
            <input
              className="kvinNetMan_input"
              value={addForm.ssid}
              onChange={(e) => setAddForm({ ...addForm, ssid: e.target.value })}
              required
              maxLength={32}
            />
          </div>
          <div className="kvinNetMan_field">
            <label className="kvinNetMan_label">Password</label>
            <input
              className="kvinNetMan_input"
              type="password"
              value={addForm.password}
              onChange={(e) =>
                setAddForm({ ...addForm, password: e.target.value })
              }
              required
              minLength={8}
              maxLength={63}
            />
          </div>
          <div className="kvinNetMan_actions">
            <button className="kvinNetMan_btn" type="submit" disabled={loading}>
              Add
            </button>
          </div>
        </form>
      </section>

      <section className="kvinNetMan_section">
        <h2 className="kvinNetMan_h2">Update Password</h2>
        <form
          className="kvinNetMan_form"
          onSubmit={handleUpdate}
          autoComplete="off"
        >
          <div className="kvinNetMan_field">
            <label className="kvinNetMan_label">SSID</label>
            <select
              className="kvinNetMan_input"
              value={updateForm.ssid}
              onChange={(e) =>
                setUpdateForm({ ...updateForm, ssid: e.target.value })
              }
              required
            >
              <option value="" disabled>
                Choose SSID
              </option>
              {networks.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div className="kvinNetMan_field">
            <label className="kvinNetMan_label">New Password</label>
            <input
              className="kvinNetMan_input"
              type="password"
              value={updateForm.password}
              onChange={(e) =>
                setUpdateForm({ ...updateForm, password: e.target.value })
              }
              required
              minLength={8}
              maxLength={63}
            />
          </div>
          <div className="kvinNetMan_actions">
            <button className="kvinNetMan_btn" type="submit" disabled={loading}>
              Update
            </button>
          </div>
        </form>
      </section>

      <footer className="kvinNetMan_footer">
        <span>Never shows passwords.</span>
      </footer>
    </div>
  );
}
